/**
 * Centralized API route constants for SignalBridge frontend
 * Use these constants instead of hardcoded strings when making API calls
 */

export const API_ROUTES = {
  // User Management
  USERS_LIST: '/api/v2/admin/users/list', // Get list of all users (v2 admin canonical)
  USERS_DIRECTORY: '/api/v2/admin/users/directory', // Superadmin directory across tenants and Dealers
  USERS_SEARCH_ADMIN: '/api/v2/admin/users/search', // Tenant-scoped user search (admin/tenant_admin)
  USERS_CREATE: '/api/users/create', // Create a new user
  USERS_UPDATE: '/api/users/update/:userId', // Update a user's information
  USERS_DELETE: '/api/users/delete/:userId', // Delete a user
  USERS_SEND_INVITE: '/api/users/send-invite',
  USERS_INVITE_STATUS: '/api/users/:uid/invite-status',

  // Agreements
  AGREEMENTS_ME_STATUS: '/api/v2/agreements/me/status',
  AGREEMENTS_ME_REQUIRED: '/api/v2/agreements/me/required',
  AGREEMENTS_ME_ACCEPT: '/api/v2/agreements/me/accept',
  ADMIN_AGREEMENT_VERSIONS: '/api/v2/admin/agreements/:agreementType/versions',
  ADMIN_USER_AGREEMENT_ACCEPTANCES: '/api/v2/admin/users/:uid/agreement-acceptances',

  // Onboarding invites (Google Auth)
  INVITES_LOOKUP: '/api/v2/invites/lookup',
  INVITES_CONSUME: '/api/v2/invites/consume',

  // Reports (Support & Feedback)
  REPORTS_LIST: '/api/v2/admin/reports/list',
  USERS_BY_EMAIL: '/api/users/by-email', // Get user by email
  USERS_SET_CLAIMS: '/api/users/set-claims', // Set user claims/permissions
  USERS_ADD_TENANT: '/api/users/add-tenant', // Add tenant to user
  USERS_REMOVE_TENANT: '/api/users/remove-tenant', // Remove tenant from user

  // Tenant Management
  // Admin (existing)
  TENANTS_LIST: '/api/v2/admin/tenants/list', // Canonical tenants list (v2 admin path)
  TENANTS_CREATE: '/api/v2/admin/tenants', // Create a new tenant (v2 admin canonical)
  TENANTS_ADD: '/api/v2/admin/tenants', // Alias for create (some code references 'add')
  TENANTS_DELETE: '/api/v2/admin/tenants/:tenantId', // Delete a tenant (DELETE)
  // Tenant SMS Config (v2 admin canonical)
  TENANT_SMS_CONFIG_ADMIN: '/api/v2/admin/tenants/:tenantId/sms-config', // Get/Update tenant SMS config
  // Tenant Voice Numbers (v2 admin canonical)
  TENANT_VOICE_NUMBERS_ADMIN: '/api/v2/admin/tenants/:tenantId/voice-numbers',
  TENANT_VOICE_NUMBER_ADMIN: '/api/v2/admin/tenants/:tenantId/voice-numbers/:numId',
  TENANT_VOICE_NUMBER_DEFAULTS_ADMIN: '/api/v2/admin/tenants/:tenantId/voice-numbers/:numId/defaults',

  // Tenant Paging (v2 admin canonical)
  TENANT_PAGING_SETTINGS_ADMIN: '/api/v2/admin/tenants/:tenantId/paging/settings',
  TENANT_PAGING_ROLE_POLICIES_ADMIN: '/api/v2/admin/tenants/:tenantId/paging/role-policies',
  TENANT_PAGING_ZONES_ADMIN: '/api/v2/admin/tenants/:tenantId/paging/zones',
  TENANT_PAGING_ZONE_ADMIN: '/api/v2/admin/tenants/:tenantId/paging/zones/:zoneId',
  TENANT_PAGING_ZONE_DEVICES_ADMIN: '/api/v2/admin/tenants/:tenantId/paging/zones/:zoneId/devices',
  TENANT_PAGING_DEVICE_ZONES_ADMIN: '/api/v2/admin/tenants/:tenantId/paging/devices/:deviceId/zones',
  TENANT_PAGING_MY_ZONES: '/api/v2/tenants/:tenantId/paging/my-zones',

  // Segments (v2 admin canonical)
  SEGMENTS_ADMIN: '/api/v2/admin/segments',
  SEGMENT_ADMIN: '/api/v2/admin/segments/:segmentId',

  // Tenant SIP Paging (v2 admin canonical)
  TENANT_SIP_PAGING_SETTINGS_ADMIN: '/api/v2/admin/tenants/:tenantId/sip-paging/settings',
  TENANT_SIP_PAGING_ROLE_POLICIES_ADMIN: '/api/v2/admin/tenants/:tenantId/sip-paging/role-policies',
  TENANT_SIP_PAGING_ZONES_ADMIN: '/api/v2/admin/tenants/:tenantId/sip-paging/zones',
  TENANT_SIP_PAGING_ZONE_ADMIN: '/api/v2/admin/tenants/:tenantId/sip-paging/zones/:zoneId',
  TENANT_SIP_PAGING_MY_ZONES: '/api/v2/tenants/:tenantId/sip-paging/my-zones',

  // Tenant SIP Paging (v2 admin canonical)
  TENANT_SIP_PAGING_SETTINGS_ADMIN: '/api/v2/admin/tenants/:tenantId/sip-paging/settings',
  TENANT_SIP_PAGING_ROLE_POLICIES_ADMIN: '/api/v2/admin/tenants/:tenantId/sip-paging/role-policies',
  TENANT_SIP_PAGING_ZONES_ADMIN: '/api/v2/admin/tenants/:tenantId/sip-paging/zones',
  TENANT_SIP_PAGING_ZONE_ADMIN: '/api/v2/admin/tenants/:tenantId/sip-paging/zones/:zoneId',
  TENANT_SIP_PAGING_MY_ZONES: '/api/v2/tenants/:tenantId/sip-paging/my-zones',

  // Image Display (user-facing)
  TENANT_IMAGE_DISPLAY_MY_ACTIONS: '/api/v2/tenants/:tenantId/image-display/my-actions',

  // Media (v2)
  MEDIA_IMAGE_DISPLAY_UPLOAD_URL: '/api/v2/media/image-display/upload-url',
  MEDIA_IMAGE_DISPLAY_DISPATCH: '/api/v2/media/image-display/dispatch',

  // Voice & PSTN (admin)
  GLOBAL_VOICE_NUMBERS_ADMIN: '/api/v2/admin/global/voice-numbers',
  VOICE_SETTINGS_ADMIN: '/api/v2/admin/voice/settings',
  VOICE_HEALTH_ADMIN: '/api/v2/admin/voice/health',
  VOICE_TELEMETRY_ADMIN: '/api/v2/admin/voice/telemetry',
  // TwiML Dashboard (admin)
  VOICE_APPS_ADMIN: '/api/v2/admin/voice/apps',
  VOICE_APP_ADMIN: '/api/v2/admin/voice/apps/:appSid',
  VOICE_CALLS_ADMIN: '/api/v2/admin/voice/calls',
  VOICE_LOGS_ADMIN: '/api/v2/admin/voice/logs',
  VOICE_IVR_ADMIN: '/api/v2/admin/voice/ivr-flows',
  VOICE_ACD_ADMIN: '/api/v2/admin/voice/acd-queues',
  VOICE_TEST_CALL_ADMIN: '/api/v2/admin/voice/test-call',
  VOICE_PLAY_MESSAGE_ADMIN: '/api/v2/admin/voice/play-message',

  // Non-admin Tenants API (v2)
  TENANTS_USER_LIST: '/api/v2/tenants/user-tenants', // List tenants accessible to current user
  TENANT_GET: '/api/v2/tenants/:tenantId', // Get tenant by id (GET) and update (PATCH)
  TENANT_DETAILS: '/api/v2/tenants/:tenantId/details', // Get tenant details/statistics
  TENANT_SWITCH_LOG: '/api/v2/tenants/log-tenant-switch', // Log tenant switch event
  // For clarity, prefer TENANT_GET (PATCH) for updates instead of admin path
  TENANTS_UPDATE: '/api/v2/tenants/:tenantId', // Update a tenant (PATCH) - non-admin v2
  USERS_ME_MEMBERSHIPS: '/api/v2/users/me/memberships',
  TENANT_MEMBERSHIPS: '/api/v2/tenants/:tenantId/memberships',
  TENANT_SIP_USERS: '/api/v2/tenants/:tenantId/sip-users',
  TENANT_SIP_CONFIG: '/api/v2/tenants/:tenantId/sip-config',
  TENANT_SIP_CREDENTIALS: '/api/v2/tenants/:tenantId/sip-credentials',
  TENANT_SIP_CREDENTIAL_PASSWORD: '/api/v2/tenants/:tenantId/sip-credentials/password',
  TENANT_SIP_CREDENTIAL_SID_PASSWORD: '/api/v2/tenants/:tenantId/sip-credentials/:credentialSid/password',
  TENANT_MEMBERSHIP_UPDATE_ADMIN: '/api/v2/admin/tenants/:tenantId/memberships/:userId',

  TENANT_AGENTS_HEALTH: '/api/v2/tenants/:tenantId/agents/health',

  // Agents (v2)
  AGENTS_LIST: '/api/v2/admin/agents/list', // Get list of agents (v2)
  AGENTS_LIST_ADMIN: '/api/v2/admin/agents/list', // Get list of agents (admin route)
  AGENT_DETAILS: '/api/v2/admin/agents/:agentId', // Get details of a specific agent
  AGENT_CREATE: '/api/v2/admin/agents', // Create a new agent
  AGENT_UPDATE: '/api/v2/admin/agents/:agentId', // Update an agent
  AGENT_DELETE: '/api/v2/admin/agents/:agentId', // Delete an agent
  AGENT_REGISTER: '/api/v2/agents/register', // Register a new agent (agent self-registration)
  AGENT_STATS: '/api/v2/admin/agents/stats', // Get agent statistics
  AGENT_STATUS_SUMMARY: '/api/v2/admin/agents/status-summary', // Get agent online/offline counts (tenant + global)
  AGENT_AUDIT: '/api/v2/admin/agents/:agentId/audit', // Get audit logs for an agent
  AGENT_RESTART: '/api/v2/admin/agents/:agentId/restart', // Restart an agent
  AGENT_CONFIG: '/api/v2/admin/agents/:agentId/config', // Admin view-specific config route (does not persist full config)
  AGENT_CONFIG_UPDATE: '/api/agent/config/update', // Dedicated config persistence endpoint (Firestore + optional MQTT)
  AGENT_SEND_MQTT_COMMAND: '/api/v2/agents/send-mqtt-command', // Publish REST command to agent via MQTT
  AGENT_COMMAND: '/api/v2/admin/agents/:agentId/command', // Send command to agent via MQTT
  AGENT_ACTIVATION_CODE: '/api/v2/admin/agents/:agentId/activation-code',
  PANEL_PAIRING_PREVIEW: '/api/v2/admin/panel-pairing/:pairingCode',
  PANEL_PAIRING_CLAIM: '/api/v2/admin/panel-pairing/:pairingCode/claim',

  // Legacy Agent Endpoints (deprecated)
  AGENTS_LIST_LEGACY: '/api/agents/list', // Legacy endpoint (deprecated)
  AGENT_DETAILS_LEGACY: '/api/agents/:agentId', // Legacy endpoint (deprecated)
  AGENT_STATS_LEGACY: '/api/agents/stats', // Legacy endpoint (deprecated)
  AGENT_AUDIT_LEGACY: '/api/agents/:agentId/audit', // Legacy endpoint (deprecated)
  AGENT_RESTART_LEGACY: '/api/agents/:agentId/restart', // Legacy endpoint (deprecated)
  AGENT_CONFIG_LEGACY: '/api/agents/:agentId/config', // Legacy endpoint (deprecated)
  AGENT_UPDATE_LEGACY: '/api/agents/:agentId', // Legacy endpoint (deprecated)
  AGENT_DELETE_LEGACY: '/api/agents/:agentId', // Legacy endpoint (deprecated)
  AGENT_CREATE_LEGACY: '/api/agents', // Legacy endpoint (deprecated)

  // Events
  EVENT_NEW: '/api/event/new', // Create a new event
  ALERT_NEW: '/api/alert/new', // Legacy (deprecated) - use ALERT_TEST or ALERTS_SEND
  ALERTS_LIST: '/api/alerts/list', // Legacy (deprecated) - use ALERT_LOGS
  ALERTS_SEND: '/api/alerts/send', // Send an alert
  ALERTS_ACKNOWLEDGE: '/api/alerts/acknowledge', // Acknowledge an alert
  ALERT_TEST: '/api/alert/test', // Test alert (no persistence)
  ALERT_LOGS: '/api/alert-logs', // Fetch alert logs (requires tenantId)
  ALERT_DELIVERY_ACK: '/api/v2/alert-delivery', // Acknowledge a Device Alert delivery (append /{eventId}/acknowledge)
  ALERT_DELIVERY_STATE: '/api/v2/alert-delivery', // Get delivery state (append /{eventId}/state)
  ALERT_DELIVERY_POLICIES: '/api/v2/alert-delivery/policies', // List or create delivery policies
  EVENT_TEMPLATES: '/api/templates', // Event templates (server redirects to flows)

  // Flows
  FLOWS: '/api/flows', // Get or create flows
  EVENT_FLOWS_CREATE: '/api/event-flows/create', // Legacy (deprecated) - no backend support
  EXECUTE_FLOW: '/api/test/execute-flow', // Execute a flow for testing
  // Flow Assignments
  FLOWS_ASSIGN: '/api/flows/assignments/assign', // Assign one or more flows to a user
  FLOWS_UNASSIGN: '/api/flows/assignments/unassign', // Unassign one or more flows from a user
  FLOWS_ASSIGNMENTS_BY_USER: '/api/flows/assignments/by-user/:userId', // List flows assigned to a user
  FLOWS_ASSIGNMENTS_BY_FLOW: '/api/flows/assignments/by-flow/:flowId', // List users assigned to a flow
  // Canonical v2 admin routes for flow assignments
  FLOWS_ASSIGN_ADMIN: '/api/v2/admin/flows/assignments/assign',
  FLOWS_UNASSIGN_ADMIN: '/api/v2/admin/flows/assignments/unassign',
  FLOWS_ASSIGNMENTS_BY_USER_ADMIN: '/api/v2/admin/flows/assignments/by-user/:userId',
  FLOWS_ASSIGNMENTS_BY_FLOW_ADMIN: '/api/v2/admin/flows/assignments/by-flow/:flowId',

  // Inbound Rules
  INBOUND_RULES_V2: '/api/v2/inbound-rules', // V2 endpoint for inbound rules
  INBOUND_RULES_ADMIN: '/api/v2/inbound-rules', // Point admin alias to v2 path (no separate admin route exists)
  INBOUND_RULES_TEST: '/api/v2/inbound-rules/test', // Test an inbound rule (v2 path)

  // Call Routing (authoritative backend writes)
  CALL_ROUTING: '/api/v2/tenants/:tenantId/call-routing',

  // Triggers
  TRIGGERS_V2: '/api/v2/triggers',

  // Actions
  ACTIONS: '/api/actions', // Base endpoint for actions
  ACTIONS_DISPATCH: '/api/actions/dispatch', // Dispatch an action
  REST_ACTIONS_LIST: '/api/v2/admin/rest-actions/list', // Canonical v2 admin REST actions list
  REST_ACTIONS_CREATE: '/api/v2/admin/rest-actions', // Canonical v2 admin REST action create

  // Webhooks
  WEBHOOKS_LIST: '/api/v2/admin/webhooks/list', // Canonical v2 admin webhooks list
  WEBHOOKS_CREATE: '/api/v2/admin/webhooks', // Canonical v2 admin webhook create
  WEBHOOKS_SEND: '/api/webhooks/send', // Send a webhook

  // Devices
  DEVICES: '/api/devices', // Base endpoint for devices
  // SIP Devices (v2 admin canonical)
  SIP_DEVICES: '/api/v2/admin/sip-devices',

  // Device Intelligence (v2)
  DEVICE_INTEL_TASK: '/api/v2/device-intel/task',
  DEVICE_INTEL_RESULT: '/api/v2/device-intel/result',
  DEVICE_INTEL_REPORTS: '/api/v2/device-intel/reports',
  DEVICE_INTEL_REPORT_DETAIL: '/api/v2/device-intel/report/:taskId',
  DEVICE_INTEL_TASKS: '/api/v2/device-intel/tasks',

  // Device Config Intelligence (v2)
  DEVICE_CONFIG_INTEL: '/api/v2/device-config',

  // Admin v2 - Device-specific (Algo 8301)
  ADMIN_DEVICE_CONFIG: '/api/v2/admin/devices/:deviceId/config',
  ADMIN_DEVICE_PRESETS: '/api/v2/admin/devices/:deviceId/presets',
  ADMIN_DEVICE_PRESET: '/api/v2/admin/devices/:deviceId/presets/:presetId',
  ADMIN_DEVICE_SCHEDULES: '/api/v2/admin/devices/:deviceId/schedules',
  ADMIN_DEVICE_SCHEDULE: '/api/v2/admin/devices/:deviceId/schedules/:scheduleId',
  ADMIN_DEVICE_TONES: '/api/v2/admin/devices/:deviceId/tones',
  ADMIN_DEVICE_TONE: '/api/v2/admin/devices/:deviceId/tones/:toneId',
  ADMIN_DEVICE_ACTION_RESTART: '/api/v2/admin/devices/:deviceId/actions/restart',
  ADMIN_DEVICE_ACTION_SYNC: '/api/v2/admin/devices/:deviceId/actions/sync',
  ADMIN_DEVICE_ACTION_RUN_PRESET: '/api/v2/admin/devices/:deviceId/actions/run-preset',

  // Test Tools
  TEST_SEND_EMAIL: '/api/test/send-email', // Send a test email
  TEST_SEND_SMS: '/api/test/send-sms', // Send a test SMS
  TEST_DEBUG_SMS: '/api/test/debug-sms', // Debug SMS
  TEST_SIP_CALL: '/api/test/sip-call', // Make a test SIP call
  TEST_SEND_SIP_CALL: '/api/test/send-sip-call', // Send a SIP call
  TEST_LAST_INCOMING_SIP: '/api/test/last-incoming-sip', // Get the last incoming SIP call
  TEST_MATCH_RULE: '/api/test/match-rule', // Test rule matching
  TEST_EMIT_DEBUG_EVENT: '/api/test/emit-debug-event', // Emit a debug incomingEvent directly (NDC)

  // Notifications
  NOTIFICATION_RESPOND: '/api/v2/notifications/respond', // Submit a notification response button click

  // Sites (Phase 1A)
  SITES_LIST: '/api/v1/sites', // List all sites for tenant
  SITES_GET: '/api/v1/sites/:siteId', // Get single site
  SITES_CREATE: '/api/v1/sites', // Create new site
  SITES_UPDATE: '/api/v1/sites/:siteId', // Update site
  SITES_DELETE: '/api/v1/sites/:siteId', // Delete site

  // Misc
  RELEASE_NOTES: '/api/release-notes', // Get or update release notes
  AUDIT_LOGS: '/api/audit-logs', // Get audit logs

  // Superadmin Firestore backup metadata (read-only)
  ADMIN_BACKUP_RECOVERY: '/api/v2/admin/backup-recovery',
  // Superadmin Twilio vendor billing (read-only)
  ADMIN_TENANT_PROFILE: '/api/v2/admin/tenants/:tenantId/profile',
  ADMIN_TENANT_SUPPORT: '/api/v2/admin/tenants/:tenantId/support',
  ADMIN_TENANT_USAGE: '/api/v2/admin/tenants/:tenantId/usage',
  ADMIN_TENANT_DELETION_INVENTORY: '/api/v2/admin/tenants/:tenantId/deletion/inventory',
  ADMIN_TENANT_DELETION_ARCHIVE: '/api/v2/admin/tenants/:tenantId/deletion/archive',
  ADMIN_TENANT_DELETION_START: '/api/v2/admin/tenants/:tenantId/deletion',
  ADMIN_TENANT_DELETION_JOB: '/api/v2/admin/tenants/:tenantId/deletion/jobs/:jobId',
  ADMIN_TENANT_DELETION_RETRY: '/api/v2/admin/tenants/:tenantId/deletion/jobs/:jobId/retry',
  ADMIN_TENANT_DELETION_RESUME: '/api/v2/admin/tenants/:tenantId/deletion/jobs/:jobId/resume',
  ADMIN_BILLING_TWILIO_SUMMARY: '/api/v2/admin/billing/twilio/summary',
  ADMIN_BILLING_TWILIO_TENANTS: '/api/v2/admin/billing/twilio/tenants',
  ADMIN_BILLING_TWILIO_ITEMS: '/api/v2/admin/billing/twilio/items',
  ADMIN_BILLING_GOOGLE_SUMMARY: '/api/v2/admin/billing/google/summary',
  ADMIN_PRICING_CATALOG: '/api/v2/admin/pricing/catalog',
  ADMIN_PRICING_INITIALIZE: '/api/v2/admin/pricing/catalog/initialize',
  ADMIN_PRICING_FEATURE: '/api/v2/admin/pricing/catalog/:featureId',
  ADMIN_PRICING_AUDIT: '/api/v2/admin/pricing/audit',
  ADMIN_PRICING_WORKBOOK: '/api/v2/admin/pricing/catalog/workbook',
  ADMIN_PRICING_WORKBOOK_PREVIEW: '/api/v2/admin/pricing/catalog/workbook/preview',
  ADMIN_PRICING_WORKBOOK_APPLY: '/api/v2/admin/pricing/catalog/workbook/apply',
  ADMIN_DEALERS: '/api/v2/admin/dealers',
  ADMIN_DEALER: '/api/v2/admin/dealers/:dealerId',
  ADMIN_DEALER_ADMINS: '/api/v2/admin/dealers/:dealerId/admins',
  ADMIN_DEALER_ADMIN: '/api/v2/admin/dealers/:dealerId/admins/:userId',
  ADMIN_DEALER_TENANTS: '/api/v2/admin/dealers/:dealerId/tenants',
  ADMIN_DEALER_TENANT: '/api/v2/admin/dealers/:dealerId/tenants/:tenantId',
  ADMIN_DEALER_ASSIGNMENTS: '/api/v2/admin/dealers/assignments',
  DEALER_CONTEXTS: '/api/v2/dealer/contexts',
  DEALER_TENANTS: '/api/v2/dealer/dealers/:dealerId/tenants',
  DEALER_TENANT: '/api/v2/dealer/dealers/:dealerId/tenants/:tenantId',
  DEALER_TENANT_SUPPORT: '/api/v2/dealer/dealers/:dealerId/tenants/:tenantId/support',
  DEALER_SUBSCRIPTION: '/api/v2/dealer/dealers/:dealerId/tenants/:tenantId/subscription',
  DEALER_SUBSCRIPTION_PREVIEW: '/api/v2/dealer/dealers/:dealerId/tenants/:tenantId/subscription/preview',
  DEALER_USAGE: '/api/v2/dealer/dealers/:dealerId/usage',
  TENANT_SUBSCRIPTION: '/api/v2/tenants/:tenantId/subscription',
  TENANT_SUBSCRIPTION_USAGE: '/api/v2/tenants/:tenantId/subscription/usage',
  ADMIN_PRICING_USAGE: '/api/v2/admin/pricing/usage',
  ADMIN_PRICING_BETA: '/api/v2/admin/pricing/beta',
};
